import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles, WandSparkles, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenerateConversationStartersResponseDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { TiptapEditor } from '@/Domain/components/Shared/Editor/TiptapEditor/TiptapEditor';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/Domain/components/Shared/Item/Item';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

const MAX_CONVERSATION_STARTERS = 3;
const MAX_CONVERSATION_STARTER_LENGTH = 100;

const ConversationStartersField = () => {
  const { t } = useTranslation(['widget']);
  const form = useFormContext<WidgetSettingsDto>();
  const queryClient = useQueryClient();
  const starters = useWatch({ control: form.control, name: 'conversation_starters' }) ?? [];
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusOnNextRender = useRef(false);

  useEffect(() => {
    if (!focusOnNextRender.current) return;
    focusOnNextRender.current = false;
    inputRefs.current[starters.length - 1]?.focus();
  }, [starters.length]);

  const generateMutation = useMutation<GenerateConversationStartersResponseDto, unknown, void>({
    mutationKey: [QueryKey.conversationStarters.generate],
    onSuccess: (data) => {
      form.setValue('conversation_starters', data.conversation_starters, {
        shouldDirty: false,
        shouldTouch: false,
      });
      queryClient.setQueryData([QueryKey.widgetSettings], (prev: WidgetSettingsDto | undefined) =>
        prev ? { ...prev, conversation_starters: data.conversation_starters } : prev,
      );
    },
  });

  const updateAt = (index: number, value: string) => {
    const next = [...starters];
    next[index] = value;
    form.setValue('conversation_starters', next, { shouldDirty: true });
  };

  const removeAt = (index: number) => {
    const next = starters.filter((_, i) => i !== index);
    form.setValue('conversation_starters', next, { shouldDirty: true });
  };

  const addStarter = () => {
    if (starters.length >= MAX_CONVERSATION_STARTERS) return;
    focusOnNextRender.current = true;
    form.setValue('conversation_starters', [...starters, ''], { shouldDirty: true });
  };

  return (
    <FormItem data-testid="field-conversation_starters">
      <FormLabel>{t('conversation_starters.label')}</FormLabel>
      <FormDescription>{t('conversation_starters.description')}</FormDescription>
      <div className="flex w-full flex-col gap-2">
        {starters.map((value, index) => (
          <div key={index} className="flex w-full items-center gap-1">
            <Input
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              size="sm"
              className="flex-1"
              value={value}
              maxLength={MAX_CONVERSATION_STARTER_LENGTH}
              placeholder={t('conversation_starters.placeholder')}
              onChange={(event) => updateAt(index, event.target.value)}
              onBlur={(event) => {
                // Mirror the backend Pydantic validator + widget-side filter, which
                // both trim and drop empties. Without this an admin who saves "  "
                // would see a stored row but no button in the widget.
                const trimmed = event.target.value.trim();
                if (trimmed !== event.target.value) updateAt(index, trimmed);
              }}
              data-testid={`field-conversation_starters-${index}`}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('conversation_starters.remove')}
              onClick={() => removeAt(index)}
              data-testid={`field-conversation_starters-remove-${index}`}
            >
              <X className="size-4" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {starters.length < MAX_CONVERSATION_STARTERS && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addStarter}
            data-testid="field-conversation_starters-add"
          >
            <Plus className="size-4" />
            {t('conversation_starters.add')}
          </Button>
        )}
        {starters.length === 0 && (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => generateMutation.mutate()}
            disabled={generateMutation.isPending}
            data-testid="field-conversation_starters-generate"
          >
            {generateMutation.isPending ? (
              <Spinner className="size-4" />
            ) : (
              <Sparkles className="size-4" />
            )}
            {generateMutation.isPending
              ? t('conversation_starters.generating')
              : t('conversation_starters.generate')}
          </Button>
        )}
      </div>
    </FormItem>
  );
};

export const TextContentSection = () => {
  const { t } = useTranslation(['widget', 'website']);
  const form = useFormContext<WidgetSettingsDto>();
  const welcomeMessage = useWatch({ control: form.control, name: 'welcome_message' });
  const isPopupDisabled = !welcomeMessage;

  return (
    <div className="flex flex-col gap-8">
      <TooltipProvider>
        <FormField
          name="header"
          render={({ field }) => (
            <FormItem data-testid="field-header">
              <FormLabel>{t('header.label')}</FormLabel>
              <FormDescription>{t('header.description')}</FormDescription>
              <FormControl>
                <Input size="sm" {...field} placeholder={t('header.placeholder')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div>
          <FormField
            name="welcome_message"
            render={({ field }) => (
              <FormItem data-testid="field-welcome_message">
                <FormLabel className="flex items-center gap-2">
                  {t('welcome_message.label')}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <WandSparkles className="w-4.5 h-4.5" />
                    </TooltipTrigger>
                    <TooltipContent>{t('welcome_message.tooltip')}</TooltipContent>
                  </Tooltip>
                </FormLabel>
                <FormDescription>{t('welcome_message.description')}</FormDescription>
                <FormControl>
                  <TiptapEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('welcome_message.placeholder')}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Item variant="muted" size="sm" className="mt-3 w-full" data-testid="field-popup_active">
            <ItemContent>
              <ItemTitle>{t('popup_active.label')}</ItemTitle>
              <ItemDescription>
                {isPopupDisabled
                  ? t('popup_active.description_disabled')
                  : t('popup_active.description')}
              </ItemDescription>
            </ItemContent>
            <ItemActions className="shrink-0">
              <FormField
                name="popup_active"
                render={({ field }) => (
                  <Switch
                    id={field.name}
                    checked={field.value && !isPopupDisabled}
                    onCheckedChange={field.onChange}
                    disabled={isPopupDisabled}
                  />
                )}
              />
            </ItemActions>
          </Item>
        </div>

        <ConversationStartersField />

        <FormField
          name="input_placeholder"
          render={({ field }) => (
            <FormItem data-testid="field-input_placeholder">
              <FormLabel>{t('input_placeholder.label')}</FormLabel>
              <FormDescription>{t('input_placeholder.description')}</FormDescription>
              <FormControl>
                <Input size="sm" {...field} placeholder={t('input_placeholder.placeholder')} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          name="background_disclaimer_text"
          render={({ field }) => (
            <FormItem data-testid="field-background_disclaimer_text">
              <FormLabel>{t('background_disclaimer_text.label')}</FormLabel>
              <FormDescription>{t('background_disclaimer_text.description')}</FormDescription>
              <FormControl>
                <TiptapEditor
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('background_disclaimer_text.placeholder')}
                  enableBubbleMenu={false}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </TooltipProvider>
    </div>
  );
};
